// WHAT: Draggable card component for Kanban board tasks
// WHY:  Provides sortable task card with drag-and-drop support and task details
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: @dnd-kit for drag-and-drop, Card component, SupabaseTask type
// RISKS:
//   - NOTE: Color mapping for priority/score/status is hardcoded - may not match design system
//   - NOTE: Drag-and-drop may have performance issues with many cards
//   - NOTE: Composite ID format may cause issues if task IDs contain pipe character

import type { SupabaseTask } from "../../hooks/data/useSupabasetasks";
import type { FC } from "react";

import { Pencil, Trash, Calendar, Star, LucideTarget, LucideZap } from "lucide-react";

import { Card } from "@/components/ui";

/**
 * WHAT:
 *   Returns color for priority level.
 *
 * @param priority - The priority string value
 *
 * @returns Hex color code for the priority
 *
 */
const getPriorityColor = (priority: string): string => {
  switch (priority.toLowerCase()) {
    case "high": {
      return "#ef4444";
    } // NOTE: red-500
    case "medium": {
      return "#f59e0b";
    } // NOTE: yellow-500
    case "low": {
      return "#10b981";
    } // NOTE: green-500
    default: {
      return "#64748b";
    } // NOTE: slate-500
  }
};

/**
 * WHAT:
 *   Returns color for task score.
 *
 * @param score - The task score (null or number)
 *
 * @returns Hex color code for the score
 *
 */
const getScoreColor = (score: null | number): string => {
  if (!score) {
    return "#94a3b8";
  } // NOTE: slate-400
  if (score >= 8) {
    return "#34d399";
  } // NOTE: green-400
  if (score >= 6) {
    return "#3b82f6";
  } // NOTE: blue-500 (tracq-bright-blue)
  if (score >= 4) {
    return "#f59e0b";
  } // NOTE: yellow-500
  return "#ef4444"; // NOTE: red-500
};

/**
 * WHAT:
 *   Returns color for task status.
 *
 * @param status - The task status string
 *
 * @returns Hex color code for the status
 *
 */
const getStatusColor = (status: null | string): string => {
  if (!status) {
    return "#64748b"; // NOTE: slate-500 (default)
  }
  switch (status) {
    case "Done": {
      return "#10b981";
    } // NOTE: green-500
    case "In Progress": {
      return "#f59e0b";
    } // NOTE: yellow-500
    case "Not Started": {
      return "#3b82f6";
    } // NOTE: blue-500 (tracq-bright-blue)
    case "Blocked": {
      return "#ef4444";
    } // NOTE: red-500
    case "Waiting": {
      return "#f97316";
    } // NOTE: orange-500 (tracq-deep-orange)
    case "Review": {
      return "#8b5cf6";
    } // NOTE: purple-500
    default: {
      return "#64748b";
    } // NOTE: slate-500
  }
};

/**
 * WHAT:
 *   Returns progress percentage for display.
 *
 * @param status - Task status
 *
 * @param progress - Task progress value
 *
 * @returns Progress percentage as number
 *
 */
const getProgressPercentage = (status: string, progress: number | undefined): number => {
  if (status === "Done") {
    return 100;
  }
  return progress ?? 0;
};

/**
 * WHAT:
 *   Draggable card component that displays task information in Kanban board
 *   with drag-and-drop support, priority indicators, and action buttons.
 *
 * WHY:
 *   Enables visual task management with drag-and-drop reordering and status
 *   updates.
 *
 * PROPS:
 *   - id?: string — Optional composite ID
 *   - task: SupabaseTask — Task data to display
 *   - onEdit?: () => void — Edit handler
 *   - onDelete?: () => void — Delete handler
 *   - isOverlay?: boolean — Whether card is being dragged (overlay state)
 *
 * BEHAVIOR:
 *   - Displays task name, deadline, score, priority, impact, effort
 *   - Shows color-coded indicators for priority/score/status
 *   - Supports drag-and-drop via @dnd-kit
 *   - Provides edit and delete actions
 *   - Handles overlay state during drag
 *
 * RISKS:
 *   - NOTE: Color mapping for priority/score/status is hardcoded - may not match design system
 *   - NOTE: Drag-and-drop may have performance issues with many cards
 *   - NOTE: Composite ID format may cause issues if task IDs contain pipe character
 *
 * @param root0
 *
 * @param root0.task
 *
 * @param root0.onEdit
 *
 * @param root0.onDelete
 *
 * @param root0.isOverlay
 *
 * @returns JSX.Element
 *
 */
export const KanbanCard: FC<{
  id?: string;
  task: SupabaseTask;
  onEdit?: () => void;
  onDelete?: () => void;
  isOverlay?: boolean;
}> = ({ isOverlay = false, onDelete, onEdit, task }) => {
  return (
    <div
      style={{
        borderLeftColor: getStatusColor(task.status),
        borderLeftWidth: "3px",
      }}
    >
      <Card
        className={`group/item p-3 bg-slate-600/30 border-slate-500/50
          transition-all duration-200 hover:border-slate-400/50 hover:bg-slate-600/50
          hover:scale-[1.02] cursor-pointer relative
          ${isOverlay ? "opacity-90 scale-105 rotate-2" : ""}`}
        variant="default"
      >
        <div className="space-y-2">
          {/* Item Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: getStatusColor(task.status),
                }}
              />
              <h5 className="text-white font-medium text-xs line-clamp-2">{task.task}</h5>
            </div>
            <div className="flex gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity duration-200">
              {onEdit ? (
                <button
                  className="text-slate-400 hover:text-tracq-bright-blue p-1 rounded-lg hover:bg-slate-500/50 transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  <Pencil size={12} />
                </button>
              ) : null}
              {onDelete ? (
                <button
                  className="text-slate-400 hover:text-red-400 p-1 rounded-lg hover:bg-slate-500/50 transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash size={12} />
                </button>
              ) : null}
            </div>
          </div>

          {/* Workspace and Aspect */}
          <div className="flex flex-wrap gap-1">
            {task.workspace ? (
              <span className="text-xs text-tracq-bright-blue bg-tracq-bright-blue/20 px-1.5 py-0.5 rounded border border-tracq-bright-blue/30 flex items-center gap-1">
                <LucideTarget size={10} />
                {task.workspace}
              </span>
            ) : null}
            {task.aspect ? (
              <span className="text-xs text-tracq-deep-orange bg-tracq-deep-orange/20 px-1.5 py-0.5 rounded border border-tracq-deep-orange/30 flex items-center gap-1">
                <LucideZap size={10} />
                {task.aspect}
              </span>
            ) : null}
          </div>

          {/* Score and Priority Row */}
          <div className="flex justify-between items-center">
            {task.score !== null && (
              <div className="flex items-center gap-1">
                <Star fill="currentColor" size={10} style={{ color: getScoreColor(task.score) }} />
                <span className="text-xs font-medium" style={{ color: getScoreColor(task.score) }}>
                  {task.score}
                </span>
              </div>
            )}

            {task.priority ? (
              <span
                className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${getPriorityColor(task.priority)}20`,
                  color: getPriorityColor(task.priority),
                }}
              >
                {task.priority}
              </span>
            ) : null}
          </div>

          {/* Deadline */}
          {task.deadline ? (
            <div className="flex items-center gap-1 pt-1 border-t border-slate-500/50">
              <Calendar className="text-slate-400" size={10} />
              <span className="text-xs text-slate-300">
                {new Date(task.deadline).toLocaleDateString("de-DE", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })}
              </span>
            </div>
          ) : null}

          {/* Impact and Effort indicators */}
          {task.impact || task.effort ? (
            <div className="flex gap-2 text-xs pt-1 border-t border-slate-500/50">
              {task.impact ? (
                <span className="text-slate-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gradient-to-r from-tracq-bright-blue to-purple-400 rounded-full" />
                  Impact: {task.impact}
                </span>
              ) : null}
              {task.effort ? (
                <span className="text-slate-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gradient-to-r from-tracq-deep-orange to-red-400 rounded-full" />
                  Effort: {task.effort}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* Progress Bar */}
          {task.progress !== null && (
            <div className="space-y-1 pt-1 border-t border-slate-500/50">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">Progress</span>
                <span
                  className={`font-medium ${
                    task.status === "Done" ? "text-green-400" : "text-tracq-bright-blue"
                  }`}
                >
                  {getProgressPercentage(task.status ?? "", task.progress)}%
                  {task.status === "Done" && <span className="ml-1 text-[10px]">(Auto)</span>}
                </span>
              </div>
              <div className="w-full bg-slate-500/50 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    task.status === "Done"
                      ? "bg-gradient-to-r from-green-500 to-green-400"
                      : "bg-gradient-to-r from-tracq-bright-blue to-blue-400"
                  }`}
                  style={{
                    width: `${String(getProgressPercentage(task.status ?? "", task.progress))}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
